/**
 * VastTracker — fires all VAST tracking pixels.
 *
 * PIXEL METHOD: always GET via new Image().src
 *   sendBeacon sends POST — ad server telemetry endpoints only accept GET.
 *   The IMA SDK uses new Image().src for exactly this reason.
 *
 * MACRO SUBSTITUTION: applied to every URL before firing
 *   [TIMESTAMP]        ISO-8601 wall-clock time
 *   [CACHEBUSTING]     8-digit random integer
 *   [CONTENTPLAYHEAD]  HH:MM:SS.mmm current video position
 *   [ASSETURI]         URL-encoded media file URI
 *   [PODSEQUENCE]      -1 for outstream
 *   [ERRORCODE]        IAB error code (fireError only)
 */
export class VastTracker {
  constructor(vastData, context = {}) {
    this.vastData = vastData;
    this._ctx = {
      assetUri: context.assetUri || '',
      podSequence: context.podSequence ?? -1,
    };
    this._q = { 0: false, 25: false, 50: false, 75: false, 100: false };
    this._imp = false;
    this._currentTime = 0;
    // Pre-process progress offsets so trackTime loop is cheap
    this._pts = (vastData.linear?.progressOffsets || []).map(o => ({ ...o, fired: false }));
  }

  setCurrentTime(ct) { this._currentTime = ct; }

  fireCreativeView() { this.fireEvent('creativeView'); }
  fireLoaded()       { this.fireEvent('loaded'); }
  fireMute()         { this.fireEvent('mute'); }
  fireUnmute()       { this.fireEvent('unmute'); }
  firePause()        { this.fireEvent('pause'); }
  fireResume()       { this.fireEvent('resume'); }
  fireSkip()         { this.fireEvent('skip'); }

  fireImpression() {
    if (this._imp) return;
    this._imp = true;
    (this.vastData.impressions || []).forEach(u => this._ping(u));
  }

  /**
   * Call on every timeupdate.
   * Auto-fires: impression, start, quartiles, complete, progress offsets.
   */
  trackTime(ct, dur) {
    if (!dur) return;
    this._currentTime = ct;
    const p = (ct / dur) * 100;
    if (!this._q[0] && ct > 0)   { this._q[0] = true;   this.fireImpression(); this.fireEvent('start'); }
    if (!this._q[25] && p >= 25)  { this._q[25] = true;  this.fireEvent('firstQuartile'); }
    if (!this._q[50] && p >= 50)  { this._q[50] = true;  this.fireEvent('midpoint'); }
    if (!this._q[75] && p >= 75)  { this._q[75] = true;  this.fireEvent('thirdQuartile'); }
    if (!this._q[100] && p >= 99) { this._q[100] = true; this.fireEvent('complete'); }
    for (const pt of this._pts) {
      if (pt.fired) continue;
      const hit = pt.pct !== null ? p >= pt.pct : ct >= pt.seconds;
      if (hit) { pt.fired = true; pt.urls.forEach(u => this._ping(u)); }
    }
  }

  fireEvent(name) {
    (this.vastData.linear?.trackingEvents?.[name] || []).forEach(u => this._ping(u));
  }

  fireClickTracking() {
    (this.vastData.linear?.clickTracking || []).forEach(u => this._ping(u));
  }

  fireError(code) {
    (this.vastData.errors || []).forEach(u =>
      this._ping(u.replace(/\[ERRORCODE\]/g, String(code)))
    );
  }

  /**
   * Fire <ViewableImpression> beacons.
   * MRC standard: 50% in-view for 2 continuous seconds = 'viewable'
   * @param {'viewable'|'notViewable'|'viewUndetermined'} outcome
   */
  fireViewableImpression(outcome) {
    const vi = this.vastData.viewableImpression;
    if (!vi) return;
    const map = {
      viewable: vi.viewable || [],
      notViewable: vi.notViewable || [],
      viewUndetermined: vi.viewUndetermined || [],
    };
    (map[outcome] || []).forEach(u => this._ping(u));
  }

  // ── Macro substitution ──────────────────────────────────────────────────

  _substitute(url) {
    if (!url) return url;
    const cb = String(Math.floor(Math.random() * 100000000)).padStart(8, '0');
    const ph = encodeURIComponent(this._fmtPH(this._currentTime));
    return url
      .replace(/\[TIMESTAMP\]/g,       encodeURIComponent(new Date().toISOString()))
      .replace(/\[CACHEBUSTING\]/g,    cb)
      .replace(/\[CONTENTPLAYHEAD\]/g, ph)
      .replace(/\[PLAYHEAD\]/g,        ph)
      .replace(/\[ASSETURI\]/g,        encodeURIComponent(this._ctx.assetUri))
      .replace(/\[PODSEQUENCE\]/g,     String(this._ctx.podSequence))
      .replace(/\[ERRORCODE\]/g,       '900')
      .replace(/\[REASON\]/g,          '')
      .replace(/\[UNIVERSALADID\]/g,   '')
      .replace(/\[ADTYPE\]/g,          'linear');
  }

  _fmtPH(sec) {
    const ms = Math.round((sec % 1) * 1000);
    const s  = Math.floor(sec) % 60;
    const m  = Math.floor(sec / 60) % 60;
    const h  = Math.floor(sec / 3600);
    return String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0') + ':' +
           String(s).padStart(2,'0') + '.' + String(ms).padStart(3,'0');
  }

  // ── Always GET — never sendBeacon (which sends POST) ───────────────────
  _ping(rawUrl) {
    const url = this._substitute(rawUrl);
    if (!url) return;
    try { new Image().src = url; } catch (_) {}
  }
}